import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styles from './RequestTable.module.scss'

import { ApiError, isApiError } from '@/api'
import AvatarImage from '@/components/AvatarImage'
import Button from '@/components/Button'
import { TeamMemberActionDialogs } from '@/components/TeamMemberActionDialogs'
import { useToast } from '@/components/Toast/ToastContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import { useTeamMemberActions } from '@/hooks/useTeamMemberActions'
import { AppDispatch } from '@/store'
import {
  deleteJoinRequestThunk,
  fetchJoinRequestsThunk,
  selectHasJoinRequestsLoaded,
  selectIsJoinRequestsLoading,
  selectJoinRequests,
} from '@/store/features/joinRequest/joinRequestSlice'
import { selectCurrentEnterprise, selectIsAdmin } from '@/store/features/user/userSlice'
import { EventName } from '@/utils/eventDefinitions'

export const RequestTable = () => {
  const hasDepts = useHasDepartments()
  const isAdmin = useSelector(selectIsAdmin)
  const enterprise = useSelector(selectCurrentEnterprise)
  const joinRequests = useSelector(selectJoinRequests)
  const hasLoaded = useSelector(selectHasJoinRequestsLoaded)
  const isLoading = useSelector(selectIsJoinRequestsLoading)
  const dispatch = useDispatch<AppDispatch>()
  const { trackEvent } = useAnalytics()
  const { updateToast } = useToast()

  const {
    approveJoinRequest,
    isLoading: isApproveLoading,
    activeDialog,
    closeDialog,
    executeAction,
  } = useTeamMemberActions()

  // Fetch join requests on mount
  useEffect(() => {
    if (!hasLoaded) dispatch(fetchJoinRequestsThunk())
  }, [dispatch, hasLoaded])

  const handleApprove = async (requestId: string, email: string) => {
    await approveJoinRequest(requestId, email)
  }

  const denyRequestAndUpdateToast = async (requestId: string) => {
    try {
      await dispatch(deleteJoinRequestThunk(requestId)).unwrap()
      updateToast('Request denied', 'success')
    } catch (error) {
      const errorMessage = 'Could not deny request. ' + extractMessage(error)
      updateToast(errorMessage, 'error')
    }
  }

  const showSkeleton = isLoading && !hasLoaded

  return (
    <>
      {/* Associated Dialogs */}
      <TeamMemberActionDialogs activeDialog={activeDialog} onClose={closeDialog} onExecute={executeAction} />

      {/* Request Table */}
      <div className={styles.container}>
        {/* Table card */}
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.headerText}>
              <span className={styles.title}>{hasDepts ? 'Join requests' : 'Team requests'}</span>
              {!enterprise?.settings?.disableTeamTrial && (
                <div className={styles.infoText}>
                  New users will be given a free trial when added to the {hasDepts ? 'organization' : 'team'}.
                </div>
              )}
            </div>
            {isAdmin && (
              <div className={styles.inviteButtonWrapper}>
                <Button
                  title={hasDepts ? 'Invite more members' : 'Invite more teammates'}
                  type="light"
                  to="/app/team"
                  onClick={() => trackEvent?.(EventName.HomeCtaClicked, { target: 'invite_teammates' })}
                />
              </div>
            )}
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.userColumn}>User</th>
                {/* <th className={styles.trialColumn}>Trial Status</th> */}
                <th className={styles.actionsColumn}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {showSkeleton ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : joinRequests.length === 0 ? (
                <>
                  <tr className={styles.emptyRow}>
                    <td colSpan={3} className={styles.emptyCell}>
                      <div className={styles.emptyState}>
                        <div className={styles.emptyTitle}>No join requests</div>
                        <div className={styles.emptyDescription}>
                          {/* eslint-disable-next-line react/no-unescaped-entities */}
                          When team members request to join, they'll appear here.
                        </div>
                      </div>
                    </td>
                  </tr>
                </>
              ) : (
                joinRequests.map((request) => (
                  <tr key={request.id} className={styles.requestRow}>
                    <td className={styles.userCell}>
                      <div className={styles.userInfo}>
                        <AvatarImage avatarUrl={request.userAvatarUrl} className={styles.avatar} />
                        <div className={styles.userDetails}>
                          <div className={styles.name}>
                            <span>
                              {request.userFirstName} {request.userLastName}{' '}
                            </span>
                          </div>
                          <div className={styles.email}>{request.userEmail}</div>
                        </div>
                      </div>
                    </td>
                    {/* <td className={styles.trialCell}>
                                            {getTrialStatusDisplay(true)}
                                            <TrialStatusInfoTooltip trialAvailable={true} />
                                        </td> */}
                    <td className={styles.actionsCell}>
                      <div className={styles.actions}>
                        <Button
                          title="Approve"
                          onClick={() => handleApprove(request.id, request.userEmail)}
                          type="light"
                          width={80}
                          isDisabled={isApproveLoading}
                        />
                        <Button
                          title="Deny"
                          onClick={() => denyRequestAndUpdateToast(request.id)}
                          type="link"
                          width={60}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

/** Extract the message from different error types */
const extractMessage = (error: Error | ApiError | unknown) => {
  if (isApiError(error)) {
    return error.detail || error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}

// Skeleton components
const SkeletonRow = () => (
  <tr className={styles.requestRow}>
    <td className={styles.userCell}>
      <div className={styles.userInfo}>
        <div className={styles.skeletonAvatar}></div>
        <div className={styles.userDetails}>
          <div className={styles.skeletonName}></div>
          <div className={styles.skeletonEmail}></div>
        </div>
      </div>
    </td>
    <td className={styles.actionsCell}>
      <div className={styles.actions}>
        <div className={styles.skeletonButton}></div>
        <div className={styles.skeletonButton}></div>
      </div>
    </td>
  </tr>
)
